// The controls that turn a dial into a decision anybody can audit, plus
// the two injection-page affordances the plan asks for.
//
// The allocation slider already existed and answers "how much of the population
// is on this arm". None of these move traffic. They answer the questions a
// register cannot answer from a dial position:
//
// What an experiment is JUDGED on is deliberately NOT one of them. A custom view
// in BI is where a measure gets picked, compared and argued with, so a second
// place to name one here could only be a weaker copy of it that then disagreed.
//
//   * GuardrailThresholds - what counts as too far, for THIS experiment?
//   * DecisionPanel    - promote or drop, which arm won, why, and the readout it
//                        was decided on, frozen.
//   * TrustedTags      - where each tagger version sits on the trust ladder, and
//                        the control that moves it (refusing `sentinel` from
//                        anything but `trusted`, because a sentinel tag is a live
//                        injection trigger the moment it lands).
//   * ShadowPreset     - "measure the trigger, send nothing".
//
// Every one writes a lifecycle row and reads it straight back, so what the page
// shows is what the audit table holds and the two cannot drift.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge, Button, TableExport } from "@recruiter/shared";
import type {
  AdminExperimentDecision,
  AdminExperimentRecord,
  AdminGuardrailOverride,
} from "@recruiter/shared";

import { decideExperiment, setGuardrailThresholds, setTagState } from "../lib/api";
import { decisionOf, formatRate } from "../lib/experimentation";
import { formatDateTime } from "../lib/format";
import { adminKeys, guardrailThresholdsQuery, tagVersionsQuery } from "../lib/queries";
import { LoadingState, errorMessage } from "./states";
import "./experiment-controls.css";

/** The kinds a threshold override and a verdict apply to.
 *  Wider than the kinds the console can ALLOCATE: `logic` and `config`
 *  experiments have no slider yet and are perfectly decidable. */
type DecidableKind = "ui" | "section" | "prompt" | "injection" | "logic" | "config";

const DECIDABLE: readonly string[] = ["ui", "section", "prompt", "injection", "logic", "config"];

export function decidableKind(kind: string): DecidableKind | null {
  return DECIDABLE.includes(kind) ? (kind as DecidableKind) : null;
}

// ───────────────────────────────────────────────────────────────────────────
// What counts as too far, for THIS experiment
// ───────────────────────────────────────────────────────────────────────────

const GUARDRAIL_HINT: Record<string, string> = {
  cost: "the multiple of the baseline's cost per call an arm may reach",
  errors: "the absolute error-rate points an arm may add over the baseline",
  latency: "the multiple of the baseline's p95 an arm may reach",
};

export function GuardrailThresholds({
  record,
  canAct,
}: {
  record: AdminExperimentRecord;
  canAct: boolean;
}) {
  const kind = decidableKind(record.kind);
  const queries = useQueryClient();
  const current = useQuery(guardrailThresholdsQuery(record.key));
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");

  const save = useMutation({
    mutationFn: () => {
      const overrides: Record<string, AdminGuardrailOverride> = {};
      for (const [name, value] of Object.entries(draft)) {
        const ratio = Number(value);
        if (value.trim() === "" || !Number.isFinite(ratio)) continue;
        // `errors` is an absolute add-on in rate points; the other two are
        // multiples of the baseline. One field either way, labelled per row.
        overrides[name] = name === "errors" ? { delta: ratio } : { ratio };
      }
      return setGuardrailThresholds(record.key, {
        kind: kind ?? "ui",
        overrides,
        reason: reason.trim(),
      });
    },
    onSuccess: () => {
      setDraft({});
      setReason("");
      void queries.invalidateQueries({ queryKey: adminKeys.experimentationGuardrails(record.key) });
      void queries.invalidateQueries({ queryKey: adminKeys.experimentation });
    },
  });

  if (!kind) return null;
  const defaults = current.data?.defaults ?? {};
  const stored = current.data?.overrides ?? {};
  const names = Object.keys(defaults);
  const edited = Object.values(draft).some((v) => v.trim() !== "");
  const ready = canAct && edited && reason.trim().length >= 3;

  return (
    <section className="exc" aria-labelledby="exc-guard-title">
      <h2 className="ad-section__title" id="exc-guard-title">
        Guardrail thresholds
      </h2>
      <p className="ad-section__sub">
        The house thresholds apply to every experiment. Override one here when this experiment is
        expected to cost or take more, so a known trade-off does not page somebody every tick.
        Anything left blank keeps the house value; the override is an audit row, not a setting.
      </p>
      {current.isPending ? <LoadingState label="Reading the thresholds…" /> : null}
      {current.isError ? <p className="exp-section__error">{errorMessage(current.error)}</p> : null}
      {names.length > 0 ? (
        <table className="exp-table exc__table">
          <TableExport name="experiment-guardrails" label="guardrails" />
          <thead>
            <tr>
              <th scope="col">Guardrail</th>
              <th scope="col">House</th>
              <th scope="col">This experiment</th>
              {canAct ? <th scope="col">Set</th> : null}
            </tr>
          </thead>
          <tbody>
            {names.map((name) => {
              const house = defaults[name];
              const mine = stored[name];
              const houseValue = name === "errors" ? house?.delta : house?.ratio;
              const myValue = name === "errors" ? mine?.delta : mine?.ratio;
              return (
                <tr key={name}>
                  <td>
                    <code>{name}</code>
                    <span className="exc__hint">{GUARDRAIL_HINT[name] ?? ""}</span>
                  </td>
                  <td>{houseValue ?? "-"}</td>
                  <td>{myValue ?? "house default"}</td>
                  {canAct ? (
                    <td>
                      <input
                        className="exc__number"
                        type="number"
                        step="0.05"
                        min="0"
                        value={draft[name] ?? ""}
                        aria-label={`${name} threshold`}
                        onChange={(e) => setDraft({ ...draft, [name]: e.target.value })}
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {canAct ? (
        <div className="exc__form">
          <input
            className="exc__reason"
            value={reason}
            placeholder="Why this experiment is allowed more (kept in the audit trail)"
            aria-label="Reason for this override"
            onChange={(e) => setReason(e.target.value)}
          />
          <Button size="sm" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Override"}
          </Button>
          {save.isError ? <p className="exp-section__error">{errorMessage(save.error)}</p> : null}
        </div>
      ) : null}
      {current.data?.setBy ? (
        <p className="exc__audit">
          Last set by {current.data.setBy} on {formatDateTime(current.data.setAt)}.
        </p>
      ) : null}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// The verdict
// ───────────────────────────────────────────────────────────────────────────

export function DecisionPanel({
  record,
  canAct,
}: {
  record: AdminExperimentRecord;
  canAct: boolean;
}) {
  const kind = decidableKind(record.kind);
  const queries = useQueryClient();
  const existing = decisionOf(record);
  const [outcome, setOutcome] = useState<"promote" | "drop">("promote");
  const [winner, setWinner] = useState(record.arms[0] ?? "");
  const [reason, setReason] = useState("");

  const decide = useMutation({
    mutationFn: () =>
      decideExperiment(record.key, {
        kind: kind ?? "ui",
        outcome,
        winnerArm: winner,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setReason("");
      void queries.invalidateQueries({ queryKey: adminKeys.experimentation });
    },
  });

  if (!kind) return null;
  const ready = canAct && winner !== "" && reason.trim().length >= 3;

  return (
    <section className="exc" aria-labelledby="exc-decide-title">
      <h2 className="ad-section__title" id="exc-decide-title">
        Decision
      </h2>
      <p className="ad-section__sub">
        Record the verdict and the readout it was taken on. The numbers are snapshotted by the
        server, not sent from this page, so the decision and its evidence cannot drift apart
        afterwards. Deciding does NOT move the dial: promoting says which arm we want, shipping it
        is a separate move with its own reason.
      </p>

      {existing ? <DecisionSummary decision={existing} /> : null}

      {canAct ? (
        <div className="exc__form">
          <label className="exc__field">
            Outcome
            <select
              className="exc__select"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value === "drop" ? "drop" : "promote")}
            >
              <option value="promote">Promote</option>
              <option value="drop">Drop</option>
            </select>
          </label>
          <label className="exc__field">
            Winning arm
            <select
              className="exc__select"
              value={winner}
              onChange={(e) => setWinner(e.target.value)}
            >
              {record.arms.map((arm, i) => (
                <option key={arm} value={arm}>
                  {arm}
                  {i === 0 ? " (baseline)" : ""}
                </option>
              ))}
            </select>
          </label>
          <input
            className="exc__reason"
            value={reason}
            placeholder="Why (this is the sentence somebody reads in three months)"
            aria-label="Reason for this decision"
            onChange={(e) => setReason(e.target.value)}
          />
          <Button size="sm" disabled={!ready || decide.isPending} onClick={() => decide.mutate()}>
            {decide.isPending ? "Recording…" : "Record the decision"}
          </Button>
          {decide.isError ? (
            <p className="exp-section__error">{errorMessage(decide.error)}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function DecisionSummary({ decision }: { decision: AdminExperimentDecision }) {
  const effect = decision.effect;
  return (
    <div className="exc__decision">
      <p className="exc__decision-line">
        <Badge tone={decision.outcome === "promote" ? "success" : "neutral"}>
          {decision.outcome}
        </Badge>{" "}
        <code>{decision.winnerArm}</code>, decided by {decision.decidedBy} on{" "}
        {formatDateTime(decision.decidedAt)}.
      </p>
      <p className="exc__decision-reason">{decision.reason}</p>
      <p className="exc__decision-effect">
        {effect
          ? `Effect at decision time: ${formatRate(effect.point)} (95% interval ${formatRate(effect.low)} to ${formatRate(effect.high)}).`
          : "No measured effect was on the readout when this was decided."}
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// The injection page's two affordances
// ───────────────────────────────────────────────────────────────────────────

/** The exposure a shadow preset uses when the experiment is currently at zero:
 *  the canary ceiling, which is as far as a first-day experiment may go anyway. */
export const SHADOW_EXPOSURE = 10;

/** "Measure the trigger, send nothing": enrol projects so every turn records
 *  whether the trigger fired, while both send probabilities stay at zero. */
export function shadowDials(current: Record<string, number>): Record<string, number> {
  const exposure = current.exposure && current.exposure > 0 ? current.exposure : SHADOW_EXPOSURE;
  return { exposure, p_triggered: 0, p_untriggered: 0 };
}

export function isShadow(dials: Record<string, number>): boolean {
  return (
    (dials.exposure ?? 0) > 0 && (dials.p_triggered ?? 0) === 0 && (dials.p_untriggered ?? 0) === 0
  );
}

export function ShadowPreset({
  dials,
  disabled,
  onApply,
}: {
  dials: Record<string, number>;
  disabled: boolean;
  onApply: (next: Record<string, number>) => void;
}) {
  return (
    <div className="exc__preset">
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || isShadow(dials)}
        onClick={() => onApply(shadowDials(dials))}
      >
        Shadow
      </Button>
      <span className="exc__preset-why">
        Shadow enrols projects and records whether the trigger fired, while sending nothing: it
        measures the trigger's rate and its onset before anybody decides the text is worth showing.{" "}
        {isShadow(dials) ? "This injection is in shadow now." : ""}
      </span>
    </div>
  );
}

/** The exploration dial, explained in the one sentence that makes it usable. */
export function ExplorationNote({ dials }: { dials: Record<string, number> }) {
  const untriggered = dials.p_untriggered ?? 0;
  return (
    <p className="exc__explore">
      The send-when-NOT-triggered dial is the exploration one: it sends the text on turns the
      trigger did not fire, which is the only way to learn whether the trigger is picking the right
      moments rather than whether the text works at all.{" "}
      {untriggered === 0
        ? "It is at zero, so this experiment measures the text on triggered turns only and cannot tell a good trigger from a good text."
        : `It is at ${untriggered}%, so that share of untriggered turns is the control for the trigger itself.`}
    </p>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// The trust ladder
// ───────────────────────────────────────────────────────────────────────────

export function TrustedTags({ canAct, enabled }: { canAct: boolean; enabled: boolean }) {
  const queries = useQueryClient();
  const versions = useQuery(tagVersionsQuery(enabled));
  const [target, setTarget] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [failed, setFailed] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: ({ versionId, state }: { versionId: string; state: string }) =>
      setTagState(versionId, { state, reason: reason.trim() }),
    onSuccess: () => {
      setReason("");
      setTarget({});
      setFailed(null);
      void queries.invalidateQueries({ queryKey: adminKeys.experimentationTags });
      // A sentinel promotion changes which names an injection's `when` may hold.
      void queries.invalidateQueries({ queryKey: adminKeys.experimentationTriggers });
    },
    onError: (err) => setFailed(errorMessage(err)),
  });

  const ladder = versions.data?.states ?? [];
  const triggerState = versions.data?.triggerState ?? "sentinel";

  return (
    <section className="exc" aria-labelledby="exc-tags-title">
      <h2 className="ad-section__title" id="exc-tags-title">
        Trusted tags
      </h2>
      <p className="ad-section__sub">
        A tag becomes an injection TRIGGER only at <code>{triggerState}</code>, and only from{" "}
        <code>trusted</code> or <code>guardrail</code>. The ladder is{" "}
        {ladder.length > 0 ? ladder.join(" to ") : "draft to probed to trusted to guardrail"}: each
        rung is a claim somebody made about a classifier, and a sentinel tag steers live prompts the
        moment it lands.
      </p>
      {versions.isPending ? <LoadingState label="Reading the ladder…" /> : null}
      {versions.isError ? (
        <p className="exp-section__error">{errorMessage(versions.error)}</p>
      ) : null}
      {failed ? <p className="exp-section__error">{failed}</p> : null}
      {versions.data && versions.data.versions.length === 0 ? (
        <p className="exp-section__empty">No tagger version has been saved yet.</p>
      ) : null}
      {versions.data && versions.data.versions.length > 0 ? (
        <>
          {canAct ? (
            <input
              className="exc__reason"
              value={reason}
              placeholder="Why this move (kept in the audit trail)"
              aria-label="Reason for a trust move"
              onChange={(e) => setReason(e.target.value)}
            />
          ) : null}
          <div className="exp-table-wrap">
            <table className="exp-table exc__table">
              <TableExport name="tagger-versions" label="tagger versions" />
              <thead>
                <tr>
                  <th scope="col">Tagger</th>
                  <th scope="col">Version</th>
                  <th scope="col">State</th>
                  <th scope="col">Would publish</th>
                  {canAct ? <th scope="col">Move to</th> : null}
                </tr>
              </thead>
              <tbody>
                {versions.data.versions.map((v) => (
                  <tr key={v.versionId}>
                    <td>
                      <code>{v.tagger}</code>
                    </td>
                    <td>
                      {v.label || v.versionId.slice(0, 8)}
                      <span className="exc__hint">{formatDateTime(v.createdAt)}</span>
                    </td>
                    <td>
                      <Badge tone={v.state === triggerState ? "success" : "neutral"}>
                        {v.state}
                      </Badge>
                    </td>
                    <td>
                      {v.triggerNames.length === 0
                        ? "-"
                        : v.triggerNames.map((n) => (
                            <code key={n} className="exc__trigger">
                              {n}
                            </code>
                          ))}
                    </td>
                    {canAct ? (
                      <td className="exc__move">
                        <select
                          className="exc__select"
                          value={target[v.versionId] ?? ""}
                          aria-label={`New state for ${v.tagger}`}
                          onChange={(e) => setTarget({ ...target, [v.versionId]: e.target.value })}
                        >
                          <option value="">keep</option>
                          {ladder
                            .filter((s) => s !== v.state)
                            .map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                        </select>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={
                            !target[v.versionId] || reason.trim().length < 3 || move.isPending
                          }
                          onClick={() =>
                            move.mutate({
                              versionId: v.versionId,
                              state: target[v.versionId] ?? "",
                            })
                          }
                        >
                          Move
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
